/** @jsxImportSource @emotion/react */
import { useState, useRef } from "react";
import { accessAPI, Titulo, Formulario, Boton, Sidebar, useIsMobile, TablaFiltrable } from "../../../shared";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Menu } from "primereact/menu";
import { Toast } from "primereact/toast";
import { Tag } from "primereact/tag";
import { confirmDialog } from "primereact/confirmdialog";
import GestionAccesos from "../../../elementos/GestionAccesos";

export default function CardUsuarios({
  usuarios,
  empleadosPendientes,
  clienteId,
  onUsuarioCreado,
  onUsuarioEditado,
  onUsuarioAccesoEliminado,
  onUsuarioEliminadoCompleto,
  onUsuarioAprobado
}) {
  const [sidebarEditarVisible, setSidebarEditarVisible] = useState(false);
  const [sidebarNuevoVisible, setSidebarNuevoVisible] = useState(false);
  const [sidebarAccesosVisible, setSidebarAccesosVisible] = useState(false);
  const [selectedUsuario, setSelectedUsuario] = useState(null);
  const [loaderEditar, setLoaderEditar] = useState(false);
  const [loaderNuevo, setLoaderNuevo] = useState(false);

  const menuRef = useRef(null);
  const toast = useRef(null);
  const isMobile = useIsMobile();

  function abrirGestionAccesos() {
    if (!selectedUsuario?.usuario?.id) return;
    setSidebarAccesosVisible(true);
  }

  function aprobarUsuarioPendiente() {
    const usuarioId = selectedUsuario?.usuario?.id;
    if (!usuarioId) return;
    const nombre = `${selectedUsuario.usuario.nombre || ""} ${
      selectedUsuario.usuario.apellido || ""
    }`.trim();
    confirmDialog({
      message: `¿Aprobar al usuario ${nombre} (${selectedUsuario.usuario.email})? Se le enviará un correo con instrucciones para crear su contraseña.`,
      header: "Aprobar usuario",
      icon: "pi pi-user-plus",
      acceptLabel: "Aprobar",
      rejectLabel: "Cancelar",
      accept: () => {
        accessAPI(
          "PUT",
          `admin/usuario/aprobar/${usuarioId}`,
          null,
          () => {
            toast.current?.show({
              severity: "success",
              summary: "Usuario aprobado",
              detail: `Se envió el correo a ${selectedUsuario.usuario.email}`,
              life: 3000
            });
            if (onUsuarioAprobado) onUsuarioAprobado();
          },
          (respuesta) => {
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail: respuesta[0]?.msg || "Error al aprobar usuario",
              sticky: true
            });
          }
        );
      }
    });
  }

  function eliminarUsuarioPendiente() {
    const usuarioId = selectedUsuario?.usuario?.id;
    if (!usuarioId) return;
    const nombre = `${selectedUsuario.usuario.nombre || ""} ${
      selectedUsuario.usuario.apellido || ""
    }`.trim();
    confirmDialog({
      message: `¿Eliminar al usuario pendiente ${nombre} (${selectedUsuario.usuario.email})? Esto no elimina al empleado, solo cancela la solicitud de usuario.`,
      header: "Eliminar usuario pendiente",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Eliminar",
      rejectLabel: "Cancelar",
      acceptClassName: "p-button-danger",
      accept: () => {
        accessAPI(
          "DELETE",
          `admin/usuario/${usuarioId}`,
          null,
          () => {
            toast.current?.show({
              severity: "success",
              summary: "Usuario pendiente eliminado",
              life: 3000
            });
            // Reutilizamos onUsuarioAprobado para refrescar el cliente
            // (carga la data de nuevo y la fila pendiente desaparece).
            if (onUsuarioAprobado) onUsuarioAprobado();
          },
          (respuesta) => {
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail: respuesta[0]?.msg || "Error al eliminar usuario",
              sticky: true
            });
          }
        );
      }
    });
  }

  const menuItems = selectedUsuario?.pendienteAprobacion
    ? [
        {
          label: "Aprobar usuario",
          icon: "pi pi-check",
          command: aprobarUsuarioPendiente
        },
        {
          label: "Eliminar usuario",
          icon: "pi pi-trash",
          command: eliminarUsuarioPendiente
        }
      ]
    : [
        {
          label: "Editar usuario",
          icon: "pi pi-pencil",
          command: () => {
            if (selectedUsuario) {
              setSidebarEditarVisible(true);
            }
          }
        },
        {
          label: "Gestionar accesos",
          icon: "pi pi-key",
          command: () => {
            abrirGestionAccesos();
          }
        }
      ];

  function nuevoUsuarioSubmit(formData) {
    setLoaderNuevo(true);
    formData.clienteid = clienteId;
    accessAPI(
      "POST",
      "admin/usuario/cliente",
      formData,
      (respuesta) => {
        setLoaderNuevo(false);
        setSidebarNuevoVisible(false);
        if (respuesta?.usuarioExistente) {
          toast.current?.show({
            severity: "info",
            summary: "Usuario existente",
            detail:
              "Ya existía un usuario con ese email en el sistema. Se le agregó el acceso a esta empresa.",
            life: 6000
          });
        }
        if (onUsuarioCreado) {
          onUsuarioCreado(respuesta);
        }
      },
      (respuesta) => {
        setLoaderNuevo(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0].msg,
          sticky: true
        });
      }
    );
  }

  const nuevoUsuarioFormConfig = {
    secciones: [
      {
        nombre: "Nuevo usuario",
        campos: [
          {
            nombre: "nombre",
            tipoitemformularioid: 1,
            etiqueta: "Nombre",
            requerido: true
          },
          {
            nombre: "apellido",
            tipoitemformularioid: 1,
            etiqueta: "Apellido",
            requerido: true
          },
          {
            nombre: "email",
            tipoitemformularioid: 2,
            etiqueta: "Email",
            requerido: true
          },
          {
            nombre: "accesoid",
            tipoitemformularioid: 5,
            etiqueta: "Nivel de acceso",
            requerido: true,
            opciones: "admin/usuario/acceso?cliente=true",
            sinOpcionInicial: true
          }
        ]
      }
    ],
    botones: [
      {
        etiqueta: "Crear usuario",
        callback: nuevoUsuarioSubmit,
        validar: true
      }
    ]
  };

  function editarUsuarioSubmit(formData) {
    setLoaderEditar(true);
    formData.clienteid = clienteId;
    accessAPI(
      "PUT",
      `admin/usuario/${selectedUsuario.usuario.id}`,
      formData,
      (respuesta) => {
        setLoaderEditar(false);
        setSidebarEditarVisible(false);
        const usuarioActualizado = respuesta[0].Usuario_x_Acceso;
        setSelectedUsuario(usuarioActualizado);
        if (onUsuarioEditado) {
          onUsuarioEditado(usuarioActualizado);
        }
      },
      (respuesta) => {
        setLoaderEditar(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0].msg,
          sticky: true
        });
      }
    );
  }

  // El form de edición solo aplica a usuarios aprobados (los pendientes
  // no tienen acceso ni nombre del Usuario, sólo se aprueban).
  const editarUsuarioFormConfig = selectedUsuario && !selectedUsuario.pendienteAprobacion
    ? {
        secciones: [
          {
            nombre: "Editar usuario",
            campos: [
              {
                nombre: "nombre",
                tipoitemformularioid: 1,
                etiqueta: "Nombre",
                requerido: true,
                respuesta: selectedUsuario.usuario.nombre
              },
              {
                nombre: "apellido",
                tipoitemformularioid: 1,
                etiqueta: "Apellido",
                requerido: true,
                respuesta: selectedUsuario.usuario.apellido
              },
              {
                nombre: "email",
                tipoitemformularioid: 2,
                etiqueta: "Email",
                requerido: true,
                respuesta: selectedUsuario.usuario.email
              },
              {
                nombre: "accesoid",
                tipoitemformularioid: 5,
                etiqueta: "Nivel de acceso",
                requerido: true,
                opciones: "admin/usuario/acceso?cliente=true",
                respuesta: selectedUsuario.acceso.id
              }
            ]
          }
        ],
        botones: [
          {
            etiqueta: "Guardar cambios",
            callback: editarUsuarioSubmit,
            validar: true
          }
        ]
      }
    : null;

  // Combina usuarios aprobados (Usuario_x_Acceso) con empleados que tienen
  // un usuario pendiente de aprobación. Las filas pendientes llevan el flag
  // pendienteAprobacion para los templates y el menú contextual.
  const filasUsuarios = (usuarios || []).map((u) => ({
    ...u,
    pendienteAprobacion: false
  }));
  const filasPendientes = (empleadosPendientes || []).map((emp) => ({
    id: `pendiente-${emp.id}`,
    pendienteAprobacion: true,
    empleadoId: emp.id,
    usuario: {
      id: emp.usuario.id,
      nombre: emp.nombre,
      apellido: emp.apellido,
      email: emp.email
    },
    acceso: null
  }));
  const filasCombinadas = [...filasUsuarios, ...filasPendientes];

  const nombreCompletoTemplate = (rowData) => {
    const nombre = `${rowData.usuario.nombre || ""} ${
      rowData.usuario.apellido || ""
    }`.trim();
    if (!rowData.pendienteAprobacion) return nombre;
    return (
      <div css={{ display: "flex", alignItems: "center", gap: "0.5em", flexWrap: "wrap" }}>
        <span>{nombre}</span>
        <Tag
          icon="pi pi-clock"
          value="Pendiente de aprobación"
          severity="warning"
          css={{ fontSize: "0.7em" }}
        />
      </div>
    );
  };

  const accesoTemplate = (rowData) => {
    return rowData.acceso?.nombre || "-";
  };

  const accionesTemplate = (rowData) => {
    return (
      <Boton
        texto
        negro
        callback={(e) => {
          e.stopPropagation();
          setSelectedUsuario(rowData);
          menuRef.current.toggle(e);
        }}
      >
        <i className="pi pi-ellipsis-v" />
      </Boton>
    );
  };

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <Menu model={menuItems} popup ref={menuRef} />

      {/* Sidebar nuevo usuario */}
      <Sidebar
        visible={sidebarNuevoVisible}
        position="right"
        onHide={() => setSidebarNuevoVisible(false)}
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Formulario config={nuevoUsuarioFormConfig} loader={loaderNuevo} />
      </Sidebar>

      {/* Sidebar editar usuario */}
      <Sidebar
        visible={sidebarEditarVisible}
        position="right"
        onHide={() => setSidebarEditarVisible(false)}
        className="w-full lg:w-30rem md:w-30rem"
      >
        {editarUsuarioFormConfig && (
          <Formulario config={editarUsuarioFormConfig} loader={loaderEditar} />
        )}
      </Sidebar>

      {/* Sidebar gestionar accesos */}
      <Sidebar
        visible={sidebarAccesosVisible}
        position="right"
        onHide={() => setSidebarAccesosVisible(false)}
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Gestionar accesos</Titulo>

        {/* El panel es el mismo que usa la ficha del colaborador. Se monta
            recién al abrir el sidebar para que recargue los accesos cada vez. */}
        {sidebarAccesosVisible && selectedUsuario?.usuario?.id && (
          <GestionAccesos
            usuarioId={selectedUsuario.usuario.id}
            toast={toast}
            onAccesoEliminado={(acceso) => {
              // Si el acceso eliminado es de este cliente, avisa al padre
              if (acceso.cliente?.id === clienteId && onUsuarioAccesoEliminado) {
                onUsuarioAccesoEliminado(acceso.id);
              }
            }}
            onUsuarioEliminado={(usuarioId) => {
              setSidebarAccesosVisible(false);
              if (onUsuarioEliminadoCompleto) {
                onUsuarioEliminadoCompleto(usuarioId);
              }
            }}
          />
        )}
      </Sidebar>

      <Card>
        <Titulo
          botones={
            <Boton
              texto
              negro
              chico
              callback={() => setSidebarNuevoVisible(true)}
            >
              <i className="pi pi-plus" />
            </Boton>
          }
        >
          Usuarios
        </Titulo>
        <TablaFiltrable conFiltros={false}
          value={filasCombinadas}
          size="small"
          emptyMessage="No hay usuarios registrados"
          dataKey="id"
          nombreArchivo="usuarios_cliente"
        >
          <Column header="Nombre" body={nombreCompletoTemplate} />
          {!isMobile && (
            <Column header="Email" body={(rowData) => rowData.usuario.email} />
          )}
          {!isMobile && <Column header="Acceso" body={accesoTemplate} />}
          <Column
            body={accionesTemplate}
            style={{ width: "60px", textAlign: "center" }}
          />
        </TablaFiltrable>
      </Card>
    </>
  );
}
